import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { 
  sendResetCode, 
  checkResetCode, 
  resetPassword, 
  clearResetError, 
  resetResetPassword 
} from "../../redux/slices/authSlice";
import { toast } from "react-toastify";
import "./ResetPassword.css";

function ResetPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    resetLoading, 
    resetError, 
    resetCodeSent, 
    resetCodeVerified 
  } = useSelector((state) => state.auth);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedCountry, setSelectedCountry] = useState("EG");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  // OTP state
  const [otp, setOtp] = useState(['', '', '', '']);
  const otpRefs = useRef([]);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: phone, 2: code, 3: password
  const [timeLeft, setTimeLeft] = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Handle reset errors
  useEffect(() => {
    if (resetError) {
      toast.error(resetError);
      dispatch(clearResetError());
    }
  }, [resetError, dispatch]);

  // Reset state when component mounts
  useEffect(() => {
    dispatch(resetResetPassword());
  }, [dispatch]);

  // Timer countdown
  useEffect(() => {
    // احذف أي تعريف أو استخدام للمتغيرات المذكورة
  }, [currentStep]);

  // عند الدخول لخطوة الكود، يبدأ التايمر
  useEffect(() => {
    if (currentStep === 2) {
      setTimeLeft(60);
      setCanResend(false);
    }
  }, [currentStep]);

  // عد تنازلي للتايمر
  useEffect(() => {
    if (currentStep === 2 && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (currentStep === 2 && timeLeft === 0) {
      setCanResend(true);
    }
  }, [timeLeft, currentStep]);

  // دالة إعادة إرسال الكود
  const handleResendCode = () => {
    const fullPhone = selectedCountryData.dialCode + phoneNumber;
    dispatch(sendResetCode({ phone: fullPhone }));
    setTimeLeft(60);
    setCanResend(false);
    setOtp(['', '', '', '']);
    otpRefs.current[0]?.focus();
    toast.info('تم إرسال كود جديد');
  };

  // قائمة الدول مع أكواد الهاتف والترجمة
  const countries = [
    { code: "EG", name: "مصر", flag: "https://flagcdn.com/w20/eg.png", dialCode: "2" },
    { code: "SA", name: "السعودية", flag: "https://flagcdn.com/w20/sa.png", dialCode: "966" },
    { code: "AE", name: "الإمارات", flag: "https://flagcdn.com/w20/ae.png", dialCode: "971" },
    { code: "KW", name: "الكويت", flag: "https://flagcdn.com/w20/kw.png", dialCode: "965" },
    { code: "QA", name: "قطر", flag: "https://flagcdn.com/w20/qa.png", dialCode: "974" },
    { code: "BH", name: "البحرين", flag: "https://flagcdn.com/w20/bh.png", dialCode: "973" },
    { code: "OM", name: "عمان", flag: "https://flagcdn.com/w20/om.png", dialCode: "968" },
    { code: "JO", name: "الأردن", flag: "https://flagcdn.com/w20/jo.png", dialCode: "962" },
    { code: "LB", name: "لبنان", flag: "https://flagcdn.com/w20/lb.png", dialCode: "961" },
    { code: "SY", name: "سوريا", flag: "https://flagcdn.com/w20/sy.png", dialCode: "963" },
    { code: "IQ", name: "العراق", flag: "https://flagcdn.com/w20/iq.png", dialCode: "964" },
    { code: "PS", name: "فلسطين", flag: "https://flagcdn.com/w20/ps.png", dialCode: "970" },
    { code: "LY", name: "ليبيا", flag: "https://flagcdn.com/w20/ly.png", dialCode: "218" },
    { code: "TN", name: "تونس", flag: "https://flagcdn.com/w20/tn.png", dialCode: "216" },
    { code: "DZ", name: "الجزائر", flag: "https://flagcdn.com/w20/dz.png", dialCode: "213" },
    { code: "MA", name: "المغرب", flag: "https://flagcdn.com/w20/ma.png", dialCode: "212" },
    { code: "SD", name: "السودان", flag: "https://flagcdn.com/w20/sd.png", dialCode: "249" },
    { code: "YE", name: "اليمن", flag: "https://flagcdn.com/w20/ye.png", dialCode: "967" },
    { code: "SO", name: "الصومال", flag: "https://flagcdn.com/w20/so.png", dialCode: "252" },
    { code: "DJ", name: "جيبوتي", flag: "https://flagcdn.com/w20/dj.png", dialCode: "253" },
    { code: "KM", name: "جزر القمر", flag: "https://flagcdn.com/w20/km.png", dialCode: "269" },
    { code: "TR", name: "تركيا", flag: "https://flagcdn.com/w20/tr.png", dialCode: "90" },
    { code: "IR", name: "إيران", flag: "https://flagcdn.com/w20/ir.png", dialCode: "98" },
    { code: "PK", name: "باكستان", flag: "https://flagcdn.com/w20/pk.png", dialCode: "92" },
    { code: "AF", name: "أفغانستان", flag: "https://flagcdn.com/w20/af.png", dialCode: "93" },
    { code: "IN", name: "الهند", flag: "https://flagcdn.com/w20/in.png", dialCode: "91" },
    { code: "BD", name: "بنغلاديش", flag: "https://flagcdn.com/w20/bd.png", dialCode: "880" },
    { code: "LK", name: "سريلانكا", flag: "https://flagcdn.com/w20/lk.png", dialCode: "94" },
    { code: "MM", name: "ميانمار", flag: "https://flagcdn.com/w20/mm.png", dialCode: "95" },
    { code: "TH", name: "تايلاند", flag: "https://flagcdn.com/w20/th.png", dialCode: "66" },
    { code: "VN", name: "فيتنام", flag: "https://flagcdn.com/w20/vn.png", dialCode: "84" },
    { code: "ID", name: "إندونيسيا", flag: "https://flagcdn.com/w20/id.png", dialCode: "62" },
    { code: "MY", name: "ماليزيا", flag: "https://flagcdn.com/w20/my.png", dialCode: "60" },
    { code: "SG", name: "سنغافورة", flag: "https://flagcdn.com/w20/sg.png", dialCode: "65" },
    { code: "PH", name: "الفلبين", flag: "https://flagcdn.com/w20/ph.png", dialCode: "63" },
    { code: "CN", name: "الصين", flag: "https://flagcdn.com/w20/cn.png", dialCode: "86" },
    { code: "JP", name: "اليابان", flag: "https://flagcdn.com/w20/jp.png", dialCode: "81" },
    { code: "KR", name: "كوريا الجنوبية", flag: "https://flagcdn.com/w20/kr.png", dialCode: "82" },
    { code: "KP", name: "كوريا الشمالية", flag: "https://flagcdn.com/w20/kp.png", dialCode: "850" },
    { code: "MN", name: "منغوليا", flag: "https://flagcdn.com/w20/mn.png", dialCode: "976" },
    { code: "KZ", name: "كازاخستان", flag: "https://flagcdn.com/w20/kz.png", dialCode: "7" },
    { code: "UZ", name: "أوزبكستان", flag: "https://flagcdn.com/w20/uz.png", dialCode: "998" },
    { code: "KG", name: "قيرغيزستان", flag: "https://flagcdn.com/w20/kg.png", dialCode: "996" },
    { code: "TJ", name: "طاجيكستان", flag: "https://flagcdn.com/w20/tj.png", dialCode: "992" },
    { code: "TM", name: "تركمانستان", flag: "https://flagcdn.com/w20/tm.png", dialCode: "993" },
    { code: "AZ", name: "أذربيجان", flag: "https://flagcdn.com/w20/az.png", dialCode: "994" },
    { code: "GE", name: "جورجيا", flag: "https://flagcdn.com/w20/ge.png", dialCode: "995" },
    { code: "AM", name: "أرمينيا", flag: "https://flagcdn.com/w20/am.png", dialCode: "374" },
    { code: "RU", name: "روسيا", flag: "https://flagcdn.com/w20/ru.png", dialCode: "7" },
    { code: "BY", name: "بيلاروسيا", flag: "https://flagcdn.com/w20/by.png", dialCode: "375" },
    { code: "UA", name: "أوكرانيا", flag: "https://flagcdn.com/w20/ua.png", dialCode: "380" },
    { code: "MD", name: "مولدوفا", flag: "https://flagcdn.com/w20/md.png", dialCode: "373" },
    { code: "RO", name: "رومانيا", flag: "https://flagcdn.com/w20/ro.png", dialCode: "40" },
    { code: "BG", name: "بلغاريا", flag: "https://flagcdn.com/w20/bg.png", dialCode: "359" },
    { code: "GR", name: "اليونان", flag: "https://flagcdn.com/w20/gr.png", dialCode: "30" },
    { code: "AL", name: "ألبانيا", flag: "https://flagcdn.com/w20/al.png", dialCode: "355" },
    { code: "MK", name: "مقدونيا الشمالية", flag: "https://flagcdn.com/w20/mk.png", dialCode: "389" },
    { code: "RS", name: "صربيا", flag: "https://flagcdn.com/w20/rs.png", dialCode: "381" },
    { code: "ME", name: "الجبل الأسود", flag: "https://flagcdn.com/w20/me.png", dialCode: "382" },
    { code: "BA", name: "البوسنة والهرسك", flag: "https://flagcdn.com/w20/ba.png", dialCode: "387" },
    { code: "HR", name: "كرواتيا", flag: "https://flagcdn.com/w20/hr.png", dialCode: "385" },
    { code: "SI", name: "سلوفينيا", flag: "https://flagcdn.com/w20/si.png", dialCode: "386" },
    { code: "HU", name: "المجر", flag: "https://flagcdn.com/w20/hu.png", dialCode: "36" },
    { code: "SK", name: "سلوفاكيا", flag: "https://flagcdn.com/w20/sk.png", dialCode: "421" },
    { code: "CZ", name: "جمهورية التشيك", flag: "https://flagcdn.com/w20/cz.png", dialCode: "420" },
    { code: "PL", name: "بولندا", flag: "https://flagcdn.com/w20/pl.png", dialCode: "48" },
    { code: "DE", name: "ألمانيا", flag: "https://flagcdn.com/w20/de.png", dialCode: "49" },
    { code: "AT", name: "النمسا", flag: "https://flagcdn.com/w20/at.png", dialCode: "43" },
    { code: "CH", name: "سويسرا", flag: "https://flagcdn.com/w20/ch.png", dialCode: "41" },
    { code: "IT", name: "إيطاليا", flag: "https://flagcdn.com/w20/it.png", dialCode: "39" },
    { code: "FR", name: "فرنسا", flag: "https://flagcdn.com/w20/fr.png", dialCode: "33" },
    { code: "ES", name: "إسبانيا", flag: "https://flagcdn.com/w20/es.png", dialCode: "34" },
    { code: "PT", name: "البرتغال", flag: "https://flagcdn.com/w20/pt.png", dialCode: "351" },
    { code: "BE", name: "بلجيكا", flag: "https://flagcdn.com/w20/be.png", dialCode: "32" },
    { code: "NL", name: "هولندا", flag: "https://flagcdn.com/w20/nl.png", dialCode: "31" },
    { code: "LU", name: "لوكسمبورغ", flag: "https://flagcdn.com/w20/lu.png", dialCode: "352" },
    { code: "DK", name: "الدنمارك", flag: "https://flagcdn.com/w20/dk.png", dialCode: "45" },
    { code: "SE", name: "السويد", flag: "https://flagcdn.com/w20/se.png", dialCode: "46" },
    { code: "NO", name: "النرويج", flag: "https://flagcdn.com/w20/no.png", dialCode: "47" },
    { code: "FI", name: "فنلندا", flag: "https://flagcdn.com/w20/fi.png", dialCode: "358" },
    { code: "IS", name: "آيسلندا", flag: "https://flagcdn.com/w20/is.png", dialCode: "354" },
    { code: "GB", name: "المملكة المتحدة", flag: "https://flagcdn.com/w20/gb.png", dialCode: "44" },
    { code: "IE", name: "أيرلندا", flag: "https://flagcdn.com/w20/ie.png", dialCode: "353" },
    { code: "US", name: "الولايات المتحدة", flag: "https://flagcdn.com/w20/us.png", dialCode: "1" },
    { code: "CA", name: "كندا", flag: "https://flagcdn.com/w20/ca.png", dialCode: "1" },
    { code: "MX", name: "المكسيك", flag: "https://flagcdn.com/w20/mx.png", dialCode: "52" },
    { code: "BR", name: "البرازيل", flag: "https://flagcdn.com/w20/br.png", dialCode: "55" },
    { code: "AR", name: "الأرجنتين", flag: "https://flagcdn.com/w20/ar.png", dialCode: "54" },
    { code: "CL", name: "تشيلي", flag: "https://flagcdn.com/w20/cl.png", dialCode: "56" },
    { code: "PE", name: "بيرو", flag: "https://flagcdn.com/w20/pe.png", dialCode: "51" },
    { code: "CO", name: "كولومبيا", flag: "https://flagcdn.com/w20/co.png", dialCode: "57" },
    { code: "VE", name: "فنزويلا", flag: "https://flagcdn.com/w20/ve.png", dialCode: "58" },
    { code: "EC", name: "الإكوادور", flag: "https://flagcdn.com/w20/ec.png", dialCode: "593" },
    { code: "BO", name: "بوليفيا", flag: "https://flagcdn.com/w20/bo.png", dialCode: "591" },
    { code: "PY", name: "باراغواي", flag: "https://flagcdn.com/w20/py.png", dialCode: "595" },
    { code: "UY", name: "أوروغواي", flag: "https://flagcdn.com/w20/uy.png", dialCode: "598" },
    { code: "GY", name: "غيانا", flag: "https://flagcdn.com/w20/gy.png", dialCode: "592" },
    { code: "SR", name: "سورينام", flag: "https://flagcdn.com/w20/sr.png", dialCode: "597" },
    { code: "GF", name: "غيانا الفرنسية", flag: "https://flagcdn.com/w20/gf.png", dialCode: "594" },
    { code: "AU", name: "أستراليا", flag: "https://flagcdn.com/w20/au.png", dialCode: "61" },
    { code: "NZ", name: "نيوزيلندا", flag: "https://flagcdn.com/w20/nz.png", dialCode: "64" },
    { code: "FJ", name: "فيجي", flag: "https://flagcdn.com/w20/fj.png", dialCode: "679" },
    { code: "PG", name: "بابوا غينيا الجديدة", flag: "https://flagcdn.com/w20/pg.png", dialCode: "675" },
    { code: "NC", name: "كاليدونيا الجديدة", flag: "https://flagcdn.com/w20/nc.png", dialCode: "687" },
    { code: "VU", name: "فانواتو", flag: "https://flagcdn.com/w20/vu.png", dialCode: "678" },
    { code: "SB", name: "جزر سليمان", flag: "https://flagcdn.com/w20/sb.png", dialCode: "677" },
    { code: "TO", name: "تونغا", flag: "https://flagcdn.com/w20/to.png", dialCode: "676" },
    { code: "WS", name: "ساموا", flag: "https://flagcdn.com/w20/ws.png", dialCode: "685" },
    { code: "KI", name: "كيريباتي", flag: "https://flagcdn.com/w20/ki.png", dialCode: "686" },
    { code: "TV", name: "توفالو", flag: "https://flagcdn.com/w20/tv.png", dialCode: "688" },
    { code: "NR", name: "ناورو", flag: "https://flagcdn.com/w20/nr.png", dialCode: "674" },
    { code: "PW", name: "بالاو", flag: "https://flagcdn.com/w20/pw.png", dialCode: "680" },
    { code: "FM", name: "ميكرونيزيا", flag: "https://flagcdn.com/w20/fm.png", dialCode: "691" },
    { code: "MH", name: "جزر مارشال", flag: "https://flagcdn.com/w20/mh.png", dialCode: "692" },
    { code: "CK", name: "جزر كوك", flag: "https://flagcdn.com/w20/ck.png", dialCode: "682" },
    { code: "NU", name: "نيوي", flag: "https://flagcdn.com/w20/nu.png", dialCode: "683" },
    { code: "TK", name: "توكيلاو", flag: "https://flagcdn.com/w20/tk.png", dialCode: "690" },
    { code: "AS", name: "ساموا الأمريكية", flag: "https://flagcdn.com/w20/as.png", dialCode: "1684" },
    { code: "GU", name: "غوام", flag: "https://flagcdn.com/w20/gu.png", dialCode: "1671" },
    { code: "MP", name: "جزر ماريانا الشمالية", flag: "https://flagcdn.com/w20/mp.png", dialCode: "1670" },
    { code: "PF", name: "بولينيزيا الفرنسية", flag: "https://flagcdn.com/w20/pf.png", dialCode: "689" },
    { code: "WF", name: "واليس وفوتونا", flag: "https://flagcdn.com/w20/wf.png", dialCode: "681" }
  ];

  const slides = [
    {
      title: "إعادة تعيين كلمة المرور",
      content: "نساعدك في استعادة الوصول إلى حسابك بسهولة وأمان من خلال خطوات بسيطة ومؤمنة."
    },
    {
      title: "حماية حسابك",
      content: "نضمن لك أمان معلوماتك الشخصية وحماية بياناتك من خلال أنظمة حماية متطورة."
    },
    {
      title: "سهولة الاستخدام",
      content: "عملية بسيطة وسريعة لإعادة تعيين كلمة المرور مع الحفاظ على أمان حسابك."
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [slides.length]);

  // إضافة انيميشن للعناصر عند التحميل
  useEffect(() => {
    const elements = document.querySelectorAll('.reset-card, .reset-info-section');
    elements.forEach((el, index) => {
      el.style.animationDelay = `${index * 0.2}s`;
    });

    // إضافة انيميشن للعناصر الداخلية
    const innerElements = document.querySelectorAll('.reset-card-title, .reset-card-subtitle, .reset-input-group, .reset-btn, .reset-card-footer, .reset-back-link');
    innerElements.forEach((el, index) => {
      el.style.animationDelay = `${(index + 2) * 0.1}s`;
    });

    // إضافة انيميشن للعناصر المعلوماتية
    const infoElements = document.querySelectorAll('.reset-brand, .reset-info-slider, .reset-indicators');
    infoElements.forEach((el, index) => {
      el.style.animationDelay = `${(index + 1) * 0.2}s`;
    });
  }, []);

  const selectedCountryData = countries.find(country => country.code === selectedCountry);

  const handleSendCode = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber.trim()) {
      toast.error('يرجى إدخال رقم الهاتف');
      return;
    }

    const fullPhone = selectedCountryData.dialCode + phoneNumber;
    
    toast.info('جاري إرسال رمز التحقق...');
    
    dispatch(sendResetCode({ phone: fullPhone }));
  };

  // handleOtpChange و handleOtpKeyDown مثل صفحة OTP
  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 3) {
        otpRefs.current[index + 1]?.focus();
      }
    }
  };
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyCode = () => {
    const fullPhone = selectedCountryData.dialCode + phoneNumber;
    const otpString = otp.join('');
    if (otpString.length !== 4) {
      toast.error('يرجى إدخال رمز التحقق المكون من 4 أرقام');
      return;
    }
    toast.info('جاري التحقق من الرمز...');
    dispatch(checkResetCode({ 
      phone: fullPhone,
      code: otpString 
    }));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!password.trim()) {
      toast.error('يرجى إدخال كلمة المرور الجديدة');
      return;
    }

    if (!confirmPassword.trim()) {
      toast.error('يرجى تأكيد كلمة المرور');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('كلمة المرور وتأكيدها غير متطابقين');
      return;
    }

    if (password.length < 6) {
      toast.error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      return;
    }

    const fullPhone = selectedCountryData.dialCode + phoneNumber;
    
    toast.info('جاري إعادة تعيين كلمة المرور...');
    
    dispatch(resetPassword({ 
      phone: fullPhone,
      password: password 
    }));
  };

  // Handle successful reset
  useEffect(() => {
    if (resetCodeSent && currentStep === 1) {
      setCurrentStep(2);
      toast.success('تم إرسال رمز التحقق بنجاح');
    }
  }, [resetCodeSent, currentStep]);

  // عند تحقق الكود بنجاح، انتقل للخطوة الثالثة
  useEffect(() => {
    if (resetCodeVerified && currentStep === 2) {
      setCurrentStep(3);
      toast.success('تم التحقق من الرمز بنجاح');
    }
  }, [resetCodeVerified, currentStep]);

  // عند إعادة تعيين كلمة المرور بنجاح، انتقل إلى صفحة تسجيل الدخول
  useEffect(() => {
    if (!resetLoading && currentStep === 3 && resetCodeVerified === false && resetCodeSent === false) {
      toast.success('تم إعادة تعيين كلمة المرور بنجاح!');
      navigate('/login');
    }
  }, [resetLoading, currentStep, resetCodeVerified, resetCodeSent, navigate]);

  const getStepTitle = () => {
    switch (currentStep) {
      case 1:
        return "إعادة تعيين كلمة المرور";
      case 2:
        return "إدخال رمز التحقق";
      case 3:
        return "كلمة المرور الجديدة";
      default:
        return "إعادة تعيين كلمة المرور";
    }
  };

  const getStepSubtitle = () => {
    switch (currentStep) {
      case 1:
        return "أدخل رقم هاتفك لإرسال رمز التحقق";
      case 2:
        return "أدخل رمز التحقق المرسل إلى هاتفك";
      case 3:
        return "أدخل كلمة المرور الجديدة";
      default:
        return "أدخل رقم هاتفك لإرسال رمز التحقق";
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <form onSubmit={handleSendCode}>
            {/* Phone Input */}
            <div className="reset-input-group">
              <input 
                type="text" 
                placeholder="رقم الهاتف" 
                className="reset-input-field"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              <div className="reset-country-selector">
                <div 
                  className={`reset-flag-icon ${showCountryDropdown ? 'open' : ''}`}
                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                >
                  <img src={selectedCountryData?.flag} alt={selectedCountryData?.code} />
                  <span className="reset-dial-code">{selectedCountryData?.dialCode}</span>
                  <span className="reset-dropdown-arrow">▼</span>
                </div>
                {showCountryDropdown && (
                  <div className="reset-country-dropdown" style={{ animation: 'slideDown 0.3s ease-out' }}>
                    {countries.map((country, index) => (
                      <div 
                        key={country.code}
                        className="reset-country-option"
                        style={{ animation: `fadeInUp 0.3s ease-out ${index * 0.05}s` }}
                        onClick={() => {
                          setSelectedCountry(country.code);
                          setShowCountryDropdown(false);
                        }}
                      >
                        <img src={country.flag} alt={country.code} />
                        <span className="reset-country-name">{country.name}</span>
                        <span className="reset-country-dial">{country.dialCode}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Button */}
            <button 
              type="submit" 
              className={`reset-btn ${resetLoading ? 'disabled' : ''}`}
              disabled={resetLoading}
            >
              {resetLoading ? 'جاري الإرسال...' : 'إرسال رمز التحقق'}
            </button>
          </form>
        );
      case 2:
        return (
          <form onSubmit={(e) => {e.preventDefault(); handleVerifyCode();}}>
            {/* OTP Inputs */}
            <div className="otp-container">
              <div className="otp-inputs" dir="ltr">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => otpRefs.current[index] = el}
                    type="text"
                    className={`otp-input ${digit ? 'filled' : ''} ${resetError ? 'error' : ''}`}
                    value={digit}
                    onChange={e => handleOtpChange(index, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(index, e)}
                    maxLength={1}
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>
            {/* تايمر وزر إعادة إرسال الكود */}
            <div style={{textAlign: 'center', marginBottom: 10}}>
              {canResend ? (
                <button type="button" className="reset-btn" onClick={handleResendCode}>
                  إعادة إرسال الكود
                </button>
              ) : (
                <span style={{color: '#888', fontSize: 14}}>يمكنك إعادة الإرسال خلال {timeLeft} ثانية</span>
              )}
            </div>
            {/* زر التحقق */}
            <button
              type="submit"
              className={`reset-btn ${otp.join('').length !== 4 || resetLoading ? 'disabled' : ''}`}
              disabled={otp.join('').length !== 4 || resetLoading}
            >
              {resetLoading ? 'جاري التحقق...' : 'تحقق من الرمز'}
            </button>
          </form>
        );
      case 3:
        return (
          <form onSubmit={handleResetPassword}>
            {/* Password Input */}
            <div className="reset-input-group">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="كلمة المرور الجديدة"
                className="reset-input-field"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            
            </div>
            {/* Confirm Password Input بدون أيقونة عين */}
            <div className="reset-input-group">
              <input
                type="password"
                placeholder="تأكيد كلمة المرور"
                className="reset-input-field"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
            {/* Button */}
            <button 
              type="submit" 
              className={`reset-btn ${resetLoading ? 'disabled' : ''}`}
              disabled={resetLoading}
            >
              {resetLoading ? 'جاري إعادة التعيين...' : 'إعادة تعيين كلمة المرور'}
            </button>
          </form>
        );
      default:
        return null;
    }
  };

  return (
    <div className="reset-page">
      <div className="reset-overlay">
        <div className="reset-wrapper">
          {/* Right Info */}
          <div className="reset-info-section">
            <h1 className="reset-brand">علمني <i className="reset-icon-book"></i></h1>
            
            <div className="reset-info-slider">
              {slides.map((slide, index) => (
                <div 
                  key={index} 
                  className={`reset-slide ${index === currentSlide ? 'active' : ''}`}
                >
                  <h3 className="reset-info-title">{slide.title}</h3>
                  <p className="reset-info-paragraph">{slide.content}</p>
                </div>
              ))}
            </div>

            {/* Indicators */}
            <div className="reset-indicators">
              {slides.map((_, index) => (
                <span 
                  key={index} 
                  className={index === currentSlide ? 'active' : ''}
                  onClick={() => setCurrentSlide(index)}
                  style={{ cursor: 'pointer' }}
                ></span>
              ))}
            </div>
          </div>

          {/* Left Form */}
          <div className="reset-card">
            <h2 className="reset-card-title">{getStepTitle()}</h2>
            <p className="reset-card-subtitle">
              {getStepSubtitle()}
            </p>

            {renderStepContent()}

            <p className="reset-card-footer">
              تذكرت كلمة المرور؟ <a href="/login" className="reset-link">تسجيل الدخول</a>
            </p>
            <a href="/" className="reset-back-link">العودة للصفحة الرئيسية</a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword; 